import React, { useEffect, useState } from "react";
import { Skeleton } from "@mui/material";
import "../process.css";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import BtnOutline from "../../../components/button/BtnOutline";
import Nodata from "../../../components/Nodata";
import { useLocation } from "react-router-dom";
import instance from "../../../api/interceptor";
import { API_PATH } from "../../../constant/apiPath";
import { Date as DateFormat } from "../../../utils/moment";
import toast from "react-hot-toast";
import { withTranslation } from "react-i18next";
import { genrateDownloadLink } from "../../../utils/utility";
import { basename } from "../../../Config";

// Image extensions that should show the IMG icon; everything else falls back to the PDF icon
const IMAGE_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "bmp",
  "webp",
  "svg",
  "tif",
  "tiff",
  "ico",
  "avif",
];

const isImageFile = (fileName) => {
  const extension = (fileName ?? "").trim().split(".").pop().toLowerCase();
  return IMAGE_EXTENSIONS.includes(extension);
};

// Root-absolute prefix for public assets (basename is served without a leading slash)
const ICON_BASE = basename ? `/${basename}` : "";

// Show the IMG icon for image files, otherwise the PDF icon
const DocIcon = ({ fileName }) =>
  isImageFile(fileName) ? (
    <img
      src={`${ICON_BASE}/icon/img_icon_doc.svg`}
      alt="document"
      width={24}
      height={24}
    />
  ) : (
    <PictureAsPdfOutlinedIcon />
  );

const Documents = ({ t, cargodetailsdata = [], setCargodetailsdata }) => {
  const location = useLocation();
  const [length, setLength] = useState(2);
  const [shipmentDocs, setShipmentDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const controller = new AbortController();
  const signal = controller.signal;
  const [checkAbort, setCheckAbort] = useState(false);
  const LocationType = location?.state?.item?.type;
  // track downloading by document IDs
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const addDownloading = (id) =>
    setDownloadingIds((prev) => new Set(prev).add(id));
  const removeDownloading = (id) =>
    setDownloadingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  const filteredDocs = Array.isArray(cargodetailsdata)
    ? cargodetailsdata.filter(
        (item) => item?.SC_DocType === "INT" || item?.SC_DocType === "MDF",
      )
    : [];

  const clickShowmore = () => {
    if (length === 2) {
      setLength(shipmentDocs.length);
    } else {
      setLength(2);
    }
  };

  const clickShowmorecargo = () => {
    if (length === 2) {
      setLength(filteredDocs.length);
    } else {
      setLength(2);
    }
  };

  const getShipmentDocs = () => {
    setLoading(true);
    const payload = {
      OrgCodes: "",
      EmailId: "",
      SortColumnName: "",
      SortDirection: "",
      CurrentPage: 0,
      PageSize: 10,
      ShipmentID: location?.state?.item?.ShipmentID ?? "",
      DocType: "",
      DocumentName: "",
      DocumentDate: "",
    };
    instance
      .post(API_PATH.GET_SHIPMENT_DOCS, payload, { signal })
      .then((res) => {
        if (res._IsSuccess) {
          setShipmentDocs(
            (res._Data || []).sort(
              (a, b) => new Date(b.SC_Date) - new Date(a.SC_Date),
            ),
          );
        } else {
          setShipmentDocs([]);
        }
      })
      .catch((err) => {
        setLoading(false);
        setShipmentDocs([]);
        console.log("shipment docs api fail", err);
      })
      .finally(() => {
        if (!checkAbort) setLoading(false);
      });
  };

  const onClickDownloadDocs = (item) => {
    const docId = item?.SC_PK;
    if (!docId) return;

    if (downloadingIds.has(docId)) return; // already downloading
    addDownloading(docId);

    // TODO: fix the location?.state?.item?.Type check to location?.state?.item?.type (small t)
    const payload = {
      OrgCodes: "",
      EmailId: "",
      SortColumnName: "",
      SortDirection: "",
      CurrentPage: 0,
      PageSize: 10,
      [location?.state?.item?.Type === "Shipment"
        ? "ShipmentID"
        : "TransportID"]: location?.state?.item?.ShipmentID ?? "",
      DocType: item?.SC_DocType ?? "",
      DocumentName: item?.SC_FileName ?? "",
      DocumentDate: item?.SC_Date ?? "",
      DocumentPk: docId,
    };

    instance
      .post(API_PATH.DOWNLOAD_SHIPMENT_DOCS, payload, { signal })
      .then((res) => {
        const prefix = genrateDownloadLink(item?.SC_FileName);
        if (prefix) {
          const a = document.createElement("a");
          a.href = `${prefix}${res?._Data?.ImageDataVC}`;
          a.download = item?.SC_FileName ?? "";
          a.click();
        } else {
          toast.error("Invalid document found!");
        }
      })
      .catch((err) => {
        console.log("download cargo docs error", err);
        toast.error("Download failed");
      })
      .finally(() => {
        removeDownloading(docId);
      });
  };

  useEffect(() => {
    const type = location?.state?.item?.type;
    if (type === "Terminal" || type === "Shipment") {
      getShipmentDocs();
    }
  }, [location?.state?.item?.ShipmentID]);

  return (
    <React.Fragment>
      {loading ? (
        <Skeleton
          variant="rectangular"
          height={110}
          style={{ width: "100%", marginBottom: "1rem" }}
        />
      ) : LocationType === "Shipment" || LocationType === "Terminal" ? (
        <div className="payment">
          <p className="color-grey2 payment-heading">{t("CargoDocuments")}</p>
          <div className="div-details-payments documents">
            {shipmentDocs.length ? (
              shipmentDocs.slice(0, length).map((item, index) => {
                if (!item) return null;
                const downloading = downloadingIds.has(item.SC_PK);
                return (
                  <div className="div-details-child" key={index}>
                    <p className="color-grey">{item?.SC_DocType ?? ""}</p>
                    <p
                      className="color-blue2"
                      disabled={downloading}
                      style={{
                        cursor: "pointer",
                        wordBreak: " break-word",
                        ...(isImageFile(item?.SC_FileName)
                          ? { display: "flex" }
                          : {}),
                      }}
                      onClick={() => onClickDownloadDocs(item)}
                    >
                      <DocIcon fileName={item?.SC_FileName} />
                      {downloading ? (
                        <span>Downloading</span>
                      ) : (
                        <span>{item.SC_FileName}</span>
                      )}
                    </p>
                    <p
                      className="payment-data"
                      style={{ fontFamily: "siena-light" }}
                    >
                      {t("Uploaded")}: {DateFormat(item?.SC_Date ?? null)}
                    </p>
                  </div>
                );
              })
            ) : (
              <Nodata />
            )}
          </div>

          {shipmentDocs && shipmentDocs.length > 2 ? (
            <div className="d-flex justify-content-end buttonline">
              <BtnOutline
                onClick={() => clickShowmore()}
                title={length <= 2 ? t("ShowMore") : t("ShowLess")}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <div className="payment">
          <p className="color-grey2 payment-heading">{t("CargoDocuments")}</p>
          <div className="div-details-payments documents">
            {filteredDocs.length ? (
              filteredDocs.slice(0, length).map((item, index) => {
                const downloading = downloadingIds.has(item.SC_PK);
                return (
                  <div className="div-details-child" key={index}>
                    <p className="color-grey">{item.SC_DocType}</p>
                    <p
                      className="color-blue2"
                      disabled={downloading}
                      style={{
                        cursor: "pointer",
                        wordBreak: " break-word",
                        ...(isImageFile(item?.SC_FileName)
                          ? { display: "flex" }
                          : {}),
                      }}
                      onClick={() => onClickDownloadDocs(item)}
                    >
                      <DocIcon fileName={item?.SC_FileName} />
                      {downloading ? (
                        <span>Downloading</span>
                      ) : (
                        <span>{item.SC_FileName}</span>
                      )}
                    </p>
                    <p
                      className="payment-data"
                      style={{ fontFamily: "siena-light" }}
                    >
                      {t("Uploaded")}: {DateFormat(item?.SC_Date ?? null)}
                    </p>
                  </div>
                );
              })
            ) : (
              <Nodata />
            )}
          </div>

          {filteredDocs.length > 2 && (
            <div className="d-flex justify-content-end buttonline">
              <BtnOutline
                onClick={() => clickShowmorecargo()}
                title={length <= 2 ? t("ShowMore") : t("ShowLess")}
              />
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default withTranslation()(React.memo(Documents));
